import { decodeRedirectUrl } from "~/utils/sanitize-redirect-url";
import { useConfig } from "~/hooks/webconfig";
import { meetupProviderReturnUrl } from "~/utils/meetup-provider-return";
import { msg } from "@lingui/core/macro";
import { ActionFunctionArgs, data, HeadersFunction, LoaderFunctionArgs, MetaFunction, redirect } from "react-router";
import { Link, useLoaderData, useLocation, useSearchParams } from "react-router";
import { appleAuthorizeUrl, encodeOAuthState } from "~/utils/oauth-state";
import { issueOAuthNonce } from "~/utils/oauth-state.server";
import React from "react";
import { Signup } from "~/components/authentication";
import { i18n } from "~/i18n/i18n";
import usePageView from "~/hooks/page-view";
import { AppRoutes } from "~/utils/app-routes";
import { isSafari } from "~/utils/extension-version";
import metrics from "~/utils/metrics";
import { openAuthWindow } from "~/utils/open-auth-window";
import { getServerClient, graphql } from "~/graphql";
import {
  accessTokenCookie,
  getAttributionFromCookie,
  loggedInCookie,
  refreshTokenCookie,
} from "~/utils/oauth.server";
import { signupDestination } from "~/utils/meetup-intent.server";
import { csrfProtection } from "~/utils/ensure-authenticated.server";

const SignupDocument = graphql(`
  mutation Signup(
    $email: String!
    $password: String!
    $state: String
    $inviteCode: String
    $origin: String
    $utm: UTMParams
  ) {
    signup(
      body: {
        password: $password
        email: $email
        state: $state
        inviteCode: $inviteCode
        origin: $origin
        utm: $utm
      }
    ) {
      refreshToken
      accessToken
    }
  }
`);

export const meta: MetaFunction = () => {
  return [
    {
      title: i18n._(msg`Mindstone - Signup`),
    },
    { name: "description", content: i18n._(msg`Signup by email to Mindstone`) },
  ];
};

// Binds every provider sign-in started on this page to this browser
// (FOX-4690): the nonce goes into the OAuth state and an HttpOnly cookie, and
// the callback refuses unless they match.
export function loader({ request }: LoaderFunctionArgs) {
  const { nonce, setCookie } = issueOAuthNonce(request);
  return data(
    { oauthNonce: nonce },
    { headers: { "Set-Cookie": setCookie, "Cache-Control": "no-store" } },
  );
}

// The document response carries the loader's no-store (a route without a
// headers export passes on only Set-Cookie): the page embeds this browser's
// nonce, so no cache should hand it to anyone else.
export const headers: HeadersFunction = ({ parentHeaders, loaderHeaders }) => {
  const merged = new Headers(parentHeaders);
  merged.set("Cache-Control", loaderHeaders.get("Cache-Control") ?? "no-store");
  return merged;
};

export async function action({ request }: ActionFunctionArgs) {
  csrfProtection(request);
  const formData = await request.formData();
  const attribution = await getAttributionFromCookie(request);

  let email = formData.get("email") as string;
  let password = formData.get("password") as string;
  let state = formData.get("state") as string;
  let inviteCode = formData.get("inviteCode") as string;
  try {
    const { data, error } = await getServerClient().mutate({
      mutation: SignupDocument,
      variables: {
        email,
        password,
        state,
        inviteCode: inviteCode || undefined,
        utm: attribution?.utm,
        origin: attribution?.origin,
      },
      fetchPolicy: "no-cache",
      context: {
        headers: {
          "x-forwarded-for":
            request.headers.get("x-forwarded-for") || undefined,
        },
      },
    });
    if (error) {
      throw error;
    }


    const headers = new Headers();
    headers.append("Set-Cookie", loggedInCookie());
    headers.append(
      "Set-Cookie",
      accessTokenCookie(data?.signup?.accessToken || ""),
    );
    headers.append(
      "Set-Cookie",
      refreshTokenCookie(data?.signup?.refreshToken || ""),
    );
    return redirect(signupDestination(request, state, formData.get("meetupIntent") as string | null), {
      headers,
      status: 302,
    });
  } catch (e) {
    // Only carry the signed meetup context forward when one was actually
    // submitted: an empty &meetupIntent= would otherwise repaint every failed
    // signup URL for every user.
    const failedIntent = String(formData.get("meetupIntent") ?? "");
    return redirect(
      `/signup?error=true&redirectUrl=${encodeURIComponent(state)}${failedIntent ? `&meetupIntent=${encodeURIComponent(failedIntent)}` : ""}`,
      {
        status: 302,
      },
    );
  }
}

export default function Index() {
  usePageView("Signup - Choice");

  const [searchParams] = useSearchParams();
  const config = useConfig();
  const previousUrl = meetupProviderReturnUrl(searchParams, config.identityUrl);
  const { oauthNonce } = useLoaderData<typeof loader>();
  const oauthState = (redirectUrl: string) =>
    encodeOAuthState({
      redirectUrl,
      inviteCode: searchParams.get("inviteCode") || "",
      nonce: oauthNonce,
    });

  const params = new URLSearchParams(useLocation().search);

  let success = false;
  if (params.get("success")) {
    success = true;
  }

  let error = false;
  if (params.get("error")) {
    error = true;
  }

  let redirectUrl = "";
  if (params.get("redirectUrl")) {
    redirectUrl = decodeRedirectUrl(params.get("redirectUrl") || "/");
  }

  const handleGoogle = () => {
    metrics.track("authentication_start", { provider: "google" });
    const clientID =
      // eslint-disable-next-line lingui/no-unlocalized-strings -- OAuth client_id, not translatable UI copy
      "398058461162-6hdd0ko3de1gmefnkpsgiidhkd364lnh.apps.googleusercontent.com";

    const { oauthGoogleRedirectUrl } = webConfig;
    const state = oauthState(previousUrl);
    openAuthWindow(
      `https://accounts.google.com/o/oauth2/v2/auth?scope=email%20profile&response_type=code&state=${encodeURIComponent(state)}&prompt=select_account&redirect_uri=${encodeURIComponent(
        oauthGoogleRedirectUrl,
      )}&client_id=${clientID}`,
      // eslint-disable-next-line lingui/no-unlocalized-strings -- window target identifier
      "Google",
      previousUrl,
      "signup",
    );
  };

  const handleApple = () => {
    metrics.track("authentication_start", { provider: "apple" });
    const clientID = "com.mindstone.web";
    const { oauthAppleRedirectUrl } = webConfig;
    // Safari: a full-page flow, so the state carries the destination and the
    // callback redirects straight to it.
    if (isSafari()) {
      window.location.href = appleAuthorizeUrl({
        clientId: clientID,
        redirectUri: oauthAppleRedirectUrl,
        state: oauthState(previousUrl),
      });
      return;
    }
    // Popup: no destination in the state, so the callback lands on the
    // success page, which messages this window.
    openAuthWindow(
      appleAuthorizeUrl({
        clientId: clientID,
        redirectUri: oauthAppleRedirectUrl,
        state: oauthState(""),
      }),
      // eslint-disable-next-line lingui/no-unlocalized-strings -- window target identifier
      "Apple",
      previousUrl,
      "signup",
    );
  };

  const handleMicrosoft = () => {
    metrics.track("authentication_start", { provider: "microsoft" });
    const { oauthMicrosoftRedirectUrl, oauthMicrosoftClientId } = config;
    const state = oauthState(previousUrl);
    openAuthWindow(
      // eslint-disable-next-line lingui/no-unlocalized-strings -- OAuth authorize URL
      `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${encodeURIComponent(
        oauthMicrosoftClientId,
      )}&response_type=code&response_mode=query&scope=openid%20profile%20email&prompt=select_account&state=${encodeURIComponent(
        state,
      )}&redirect_uri=${encodeURIComponent(oauthMicrosoftRedirectUrl)}`,
      // eslint-disable-next-line lingui/no-unlocalized-strings -- window target identifier
      "Microsoft",
      previousUrl,
      "signup",
    );
  };
  // Microsoft sign-in is configured per environment (OAUTH_MICROSOFT_CLIENT_ID,
  // served through the config query); no client id ⇒ no button.
  const microsoftConfigured = (config.oauthMicrosoftClientId ?? "") !== "";

  const handleLinkedin = () => {
    metrics.track("authentication_start", { provider: "linkedin" });
    // eslint-disable-next-line lingui/no-unlocalized-strings -- OAuth client ID
    const clientID = "78y1d813p23i03";

    const { oauthLinkedinRedirectUrl } = webConfig;
    const state = oauthState(previousUrl);
    openAuthWindow(
      `https://www.linkedin.com/oauth/v2/authorization?scope=openid%20profile%20email&response_type=code&state=${encodeURIComponent(state)}&redirect_uri=${encodeURIComponent(
        oauthLinkedinRedirectUrl,
      )}&client_id=${clientID}`,
      // eslint-disable-next-line lingui/no-unlocalized-strings -- window target identifier
      "Linkedin",
      previousUrl,
      "signup",
    );
  };

  return (
    <Signup
      handleGoogle={handleGoogle}
      handleApple={handleApple}
      handleLinkedin={undefined}
      handleMicrosoft={microsoftConfigured ? handleMicrosoft : undefined}
      success={success}
      error={error}
      appRoutes={AppRoutes}
      routerUtils={{
        useLocation,
        Link,
      }}
    />
  );
}
